import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useScreenWidth } from "@msys/ui";
import { Box, Container } from "@mui/material";
import { pick } from "lodash-es";
import React from "react";
import { Outlet, useMatch, useNavigate } from "react-router-dom";
import { useUpdateEffect } from "react-use";
import {
  Agreement,
  ItemType,
  PermissionName,
} from "../../../clients/graphqlTypes.js";
import { useFeature } from "../../../common/FeatureFlags.js";
import { useUserData } from "../../auth/useUserData.js";
import { usePageWidth } from "../../commons/hooks/usePageWidth.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageColumn } from "../../commons/layout/PageColumn.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageHeader } from "../../commons/layout/PageHeader.js";
import { Stack } from "../../commons/layout/Stack.js";
import {
  ConfirmModalProps,
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../commons/modals/ConfirmProcess.js";
import { REQUIREMENT_ITEM_TYPES } from "../../constants.js";
import { isAllowedToPaste } from "../../features/doc-items/constraints.js";
import {
  getViewerDecisionRole,
  isTreePreviewItemVisible,
} from "../../features/doc-items/helpers.js";
import {
  CreateItemFromTemplateArgs,
  CreateItemsFromTemplatesArgs,
  CreateItemsWithProductsArgs,
  useCreateItem,
} from "../../features/doc-items/hooks/useCreateItem.js";
import { useDocItemsClipboard } from "../../features/doc-items/hooks/useDocItemsClipboard.js";
import { DecisionsInBulkProcessRef } from "../../features/doc-items/modals/DecisionsInBulkProcess.js";
import {
  createTreeItem,
  createTreeItemInput,
} from "../../features/projects/requirement-trees.js";
import { RequirementAction } from "../../features/requirements/RequirementAction.js";
import { RequirementDecisionsInBulkProcess } from "../../features/requirements/RequirementDecisionsInBulkProcess.js";
import { RequirementPreview } from "../../features/requirements/RequirementPreview.js";
import { RequirementSubHeader } from "../../features/requirements/RequirementSubHeader.js";
import { shouldRenderRequirementCreateInput } from "../../features/requirements/helpers.js";
import { VirtualItemTree } from "../../trees-virtual/VirtualItemTree.js";
import { VirtualBareTreeStandaloneItem } from "../../trees-virtual/components/VirtualBareTreeStandaloneItem.js";
import {
  useEnrichExpandedStoreWithParentPathIds,
  useExpandedStoreWithLocalStorage,
} from "../../trees-virtual/hooks/useExpandedStore.js";
import { AdditionalItemInput } from "../../trees-virtual/types.js";
import { TreeToggleAllExpandedButton } from "../../trees/components/TreeToggleButton.js";
import {
  createRecursiveTryFocusElementFunction,
  isInTestMode,
  nullFn,
} from "../../utils.js";
import { DocShareBox } from "../documents/DocShareBox.js";
import { useProjectRequirementContext } from "./ProjectRequirementContext.js";
import {
  RequirementEdit_ItemFragment,
  useProjectRequirementEdit_CreateItemsFromTemplatesMutation,
  useProjectRequirementEdit_CreateItemsMutation,
  useProjectRequirementEdit_PasteItemFromClipboardMutation,
  useProjectRequirementEditQuery,
} from "./ProjectRequirementEdit.generated.js";

interface Props {
  projectId: string;
  requirementId: string;
  prefixBreadcrumbs: BreadcrumbItem[];
  submenuItems: PageTopbarItem[];
  activeSubmenuItem: PageTopbarItem | undefined;
}

export const ProjectRequirementEdit = ({
  projectId,
  requirementId,
  submenuItems,
  activeSubmenuItem,
  prefixBreadcrumbs,
}: Props) => {
  const pathToProject = `/projects/${projectId}`;
  const pathToDocList = `/projects/${projectId}/requirements`;
  const pathToDoc = `/projects/${projectId}/requirements/${requirementId}`;
  const pathToDocPage = `${pathToDoc}/edit`;

  const match = useMatch(`${pathToDocPage}/items/:itemId`);
  const selectedItemId = match?.params?.itemId ?? null;

  const [additionalItemInput, setAdditionalItemInput] =
    React.useState<AdditionalItemInput | null>(null);

  const onAdditionalItemInputChange = React.useCallback(
    (input: AdditionalItemInput) => {
      setAdditionalItemInput(input);
      setTimeout(() => {
        const inputRank =
          input.itemRank + (input.position === "below" ? 0.5 : -0.5);
        if (!isInTestMode())
          createRecursiveTryFocusElementFunction(
            `input#msys-tree-input-${input.itemParentType}-${input.itemParentId}[data-node-rank="${inputRank}"]:enabled`
          )();
      });
    },
    []
  );

  const navigate = useNavigate();
  const viewer = useUserData().currentUser!;

  const { isMinOneColumnWithPreview, isMinTwoColumnsWithPreview } =
    usePageWidth();

  const { isMinTablet } = useScreenWidth();

  const [treeContainer, setTreeContainer] =
    React.useState<HTMLDivElement | null>(null);
  const [previewContainer, setPreviewContainer] =
    React.useState<HTMLDivElement | null>(null);

  const expandedStore = useExpandedStoreWithLocalStorage(
    "requirement",
    requirementId,
    selectedItemId
  );

  const client = useApolloClient();
  const query = useProjectRequirementEditQuery({
    client,
    variables: {
      quoteId: requirementId,
      projectId,
      expandedItemIds: expandedStore.expandedItemIds,
    },
  });

  const project = getDataOrNull(query.data?.project)?.project;
  const doc = getDataOrNull(query.data?.requirement)?.requirement;
  const docTitle = doc?.title ?? "";

  const canFinalize =
    viewer.organisation.id === doc?.owningSystemOrganisationId;

  const canEdit = viewer.organisation.id === doc?.owningSystemOrganisationId;

  const navigateToItem = React.useCallback(
    (id: string) => navigate(`${pathToDocPage}/items/${id}`, { replace: true }),
    [navigate, pathToDocPage]
  );

  const viewerIsContractor = true; // project?.contractor?.id === viewer.organisation.id; // TODO-MODEL2022 fix!

  const viewerDecisionRole = getViewerDecisionRole(doc?.actors ?? []);

  const viewerPermissions: PermissionName[] = React.useMemo(
    () => doc?.viewerPermissions ?? [],
    [doc?.viewerPermissions]
  );

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const startConfirmProcess = React.useCallback((props: ConfirmModalProps) => {
    return confirmProcessRef.current!.startConfirmProcess(props);
  }, []);

  const decisionsInBulkProcessRef =
    React.useRef<DecisionsInBulkProcessRef>(null);
  const startDecisionsInBulkProcess = React.useCallback(() => {
    decisionsInBulkProcessRef.current?.startDecisionsInBulkProcess();
  }, []);

  const { refetchWizardQuestions } = useProjectRequirementContext();
  const {
    createItem,
    createItemByType,
    createItemFromTemplate,
    createItemsFromTemplates,
    createItemsWithProducts,
    pasteItem,
    isAllowedToPasteItem,
  } = useCreateRequirementItems({
    projectId,
    requirementId,
    expandedItemIds: expandedStore.expandedItemIds,
    onItemCreated: refetchWizardQuestions,
  });

  // TODO: STRIPDOWN - why do we need agreement on the requirement doc?
  const agreement: Agreement = "NONE";

  const TreeItem = React.useMemo(() => {
    if (!doc?.id || agreement === undefined) return nullFn;

    return createTreeItem({
      pathToDocPage,
      requirementId,
      projectId,
      document: { id: doc?.id, viewerPermissions },
      docAgreement: agreement,
      startConfirmProcess,
      expandedItemIds: expandedStore.expandedItemIds,
      setItemExpanded: expandedStore.setItemExpanded,
      pasteItem,
      isAllowedToPasteItem,
      onAdditionalItemInputChange,
    });
  }, [
    doc?.id,
    pathToDocPage,
    requirementId,
    projectId,
    viewerPermissions,
    startConfirmProcess,
    expandedStore.expandedItemIds,
    expandedStore.setItemExpanded,
    pasteItem,
    isAllowedToPasteItem,
    onAdditionalItemInputChange,
  ]);

  const TreeItemInput = React.useMemo(
    () =>
      createTreeItemInput({
        requirementId,
        createItem,
        createItemByType,
        createItemFromTemplate,
        createItemsFromTemplates,
        createItemsWithProducts,
        pasteItem,
        isAllowedToPasteItem,
      }),
    [
      createItem,
      createItemByType,
      createItemFromTemplate,
      createItemsFromTemplates,
      createItemsWithProducts,
      isAllowedToPasteItem,
      pasteItem,
      requirementId,
    ]
  );

  const [filteredItems, rootItem, allDocItems] = React.useMemo(() => {
    if (!doc) return [[], undefined, []];
    const allDocItems = doc.items;
    const rootItem = allDocItems?.find(item => item.isRootItem);
    const treeItems = allDocItems
      .filter(e => e.authorOrganisationId === viewer.organisation.id)
      .filter(item => !item.isAnyParentItemEliminated);
    return [treeItems, rootItem, allDocItems];
  }, [doc, viewer.organisation.id]);

  useUpdateEffect(() => {
    setAdditionalItemInput(null);
  }, [selectedItemId, allDocItems]);

  useEnrichExpandedStoreWithParentPathIds(
    expandedStore,
    allDocItems,
    selectedItemId,
    !query.loading && !query.error
  );

  const isRootSelected = selectedItemId === rootItem?.id;

  const filterItem = React.useCallback(
    (i: RequirementEdit_ItemFragment) =>
      isTreePreviewItemVisible(viewerIsContractor, i, allDocItems, false),
    [allDocItems, viewerIsContractor]
  );

  React.useEffect(() => {
    if (
      !query.loading &&
      selectedItemId &&
      !filteredItems.some(item => item.id === selectedItemId) &&
      selectedItemId !== rootItem?.id
    ) {
      navigate(pathToDocPage, { replace: true });
    }
  }, [
    selectedItemId,
    filteredItems,
    pathToDocPage,
    rootItem,
    query.loading,
    navigate,
  ]);

  const docSharingEnabled = useFeature("DocSharing");
  const viewerPermissionDetailsEnabled = useFeature("ViewerPermissionDetails");

  return (
    <Page
      subtitle={project?.title}
      title={docTitle}
      submenuItems={submenuItems}
      breadcrumbs={prefixBreadcrumbs}
      header={
        <PageHeader
          breadcrumbs={prefixBreadcrumbs}
          submenuItems={submenuItems}
          activeSubmenuItem={activeSubmenuItem}
        />
      }
      subHeader={
        project && doc ? (
          <RequirementSubHeader
            project={project}
            requirement={doc}
            activeView="tree"
            canEdit={canEdit}
            pathToDoc={pathToDoc}
            pathToDocList={pathToDocList}
            pathToDocPage={pathToDocPage}
            isHeaderVisible={undefined as never}
            setHeaderVisible={undefined as never}
          />
        ) : undefined
      }
      action={
        project && doc ? (
          <RequirementAction
            doc={doc}
            project={project}
            pathToProject={pathToProject}
            startDecisionsInBulkProcess={startDecisionsInBulkProcess}
          />
        ) : undefined
      }
    >
      <PageColumn
        $hasRightBorder
        $hasBackground
        $display="block"
        ref={isMinTablet ? setTreeContainer : undefined}
      >
        {project && doc && rootItem && (
          <Stack flexDirection={"column"} spacing={2}>
            <Box>
              <TreeToggleAllExpandedButton
                type="button"
                areAllItemsExpanded={expandedStore.areAllItemsExpanded}
                setAllItemsExpanded={expandedStore.setAllItemsExpanded}
              />
            </Box>

            <Box>
              <VirtualBareTreeStandaloneItem
                item={rootItem}
                items={filteredItems}
                allItems={allDocItems}
                depth={0}
                itemComponent={TreeItem}
                selected={isRootSelected}
                sticky={isRootSelected}
                top={0}
                clickable
                onClick={() => navigateToItem(rootItem.id)}
                to={`${pathToDocPage}/items/${rootItem.id}`}
              />

              <VirtualItemTree<RequirementEdit_ItemFragment, true>
                docId={doc.id}
                projectId={projectId}
                items={filteredItems}
                allItems={allDocItems}
                filterItem={filterItem}
                selectedItemId={selectedItemId}
                enableCreating={true}
                enableDragging={true}
                shouldRenderCreateInput={shouldRenderRequirementCreateInput}
                documentItemTypes={REQUIREMENT_ITEM_TYPES}
                container={treeContainer}
                itemComponent={TreeItem}
                inputComponent={TreeItemInput}
                expandedStore={expandedStore}
                additionalItemInput={additionalItemInput}
              />
            </Box>

            <RequirementDecisionsInBulkProcess
              ref={decisionsInBulkProcessRef}
              projectId={projectId}
              requirementId={doc.id}
              canFinalize={canFinalize}
              externalExpandedItemIds={expandedStore.expandedItemIds}
            />
          </Stack>
        )}
      </PageColumn>
      <ConfirmProcess ref={confirmProcessRef} />

      <Outlet
        context={{
          expandedItemIds: expandedStore.expandedItemIds,
          CreateChildComponent: TreeItemInput,
          pasteItem,
          isAllowedToPasteItem,
          viewerDecisionRole,
        }}
      />

      {project && doc && isMinOneColumnWithPreview && (
        <PageContainer
          $fullHeight
          onClick={() => {
            if (!isMinTwoColumnsWithPreview)
              navigate(pathToDocPage, { replace: true });
          }}
          ref={setPreviewContainer}
        >
          <Container maxWidth="md">
            {viewerPermissionDetailsEnabled ? (
              <div>
                <h2>viewer permission details</h2>
                <hr />
                <textarea
                  style={{ width: "100%", height: 50 }}
                  readOnly
                  defaultValue={JSON.stringify(doc.viewerPermissions, null, 2)}
                ></textarea>
              </div>
            ) : null}

            {docSharingEnabled ? (
              <DocShareBox
                docType="REQUIREMENT"
                docId={doc.id}
                projectId={project.id}
              />
            ) : null}

            <RequirementPreview
              requirement={doc}
              navigateToItem={({ id }) => navigateToItem(id)}
              selectedItemId={selectedItemId}
              canFinalize={canFinalize}
              expandedStore={expandedStore}
              expandedItemIds={expandedStore.expandedItemIds}
              container={previewContainer}
            />
          </Container>
        </PageContainer>
      )}
    </Page>
  );
};

export function useCreateRequirementItems({
  projectId,
  requirementId,
  expandedItemIds,
  onItemCreated,
}: {
  projectId: string;
  requirementId: string;
  expandedItemIds: string[] | undefined;
  onItemCreated?(): Promise<void> | void; // should be memoized
}) {
  const client = useApolloClient();
  const [createItems] = useProjectRequirementEdit_CreateItemsMutation({
    client,
  });

  const { createItem, createItemByType } = useCreateItem({
    projectId,
    createDocumentItems: createItems,
    expandedItemIds,
    onItemCreated,
  });

  const [createItemsFromTemplatesMutation] =
    useProjectRequirementEdit_CreateItemsFromTemplatesMutation({
      client,
    });

  const createItemFromTemplate = React.useCallback(
    async ({
      parentItemId,
      templateQuoteId,
      newItemRank,
    }: CreateItemFromTemplateArgs) => {
      const result = await createItemsFromTemplatesMutation({
        variables: {
          input: {
            parentProjectId: projectId,
            parentDocId: requirementId,
            parentItemId,
            templates: [{ templateQuoteId }],
            expandedItemIds,
            rank: newItemRank,
          },
        },
      });
      await onItemCreated?.();
      return result.data?.createItemsFromTemplates.items[0];
    },
    [
      createItemsFromTemplatesMutation,
      expandedItemIds,
      onItemCreated,
      projectId,
      requirementId,
    ]
  );

  const createItemsFromTemplates = React.useCallback(
    async ({
      parentItemId,
      templates,
      newItemRank,
    }: CreateItemsFromTemplatesArgs) => {
      const result = await createItemsFromTemplatesMutation({
        variables: {
          input: {
            parentProjectId: projectId,
            parentDocId: requirementId,
            parentItemId,
            templates: templates.map(tpl => ({
              templateQuoteId: tpl.template.id,
              templateEstimatedQuantity: tpl.quantity,
            })),
            expandedItemIds,
            rank: newItemRank,
          },
        },
      });
      await onItemCreated?.();
      return result.data?.createItemsFromTemplates.items;
    },
    [
      createItemsFromTemplatesMutation,
      expandedItemIds,
      onItemCreated,
      projectId,
      requirementId,
    ]
  );

  const createItemsWithProducts = React.useCallback(
    async ({
      parentItemId,
      products,
      newItemRank,
    }: CreateItemsWithProductsArgs) => {
      const result = await createItems({
        variables: {
          input: {
            projectId,
            docId: requirementId,
            parentItemId,
            items: products.map(opts =>
              "__typename" in opts.product
                ? {
                    title: opts.product.texts?.title ?? "",
                    type: "paid",
                    decisionBehaviorOfSubitems: "NONE",
                    estimatedQuantity: opts.quantity,
                    product: pick(opts.product, "supplierId", "articleNumber"),
                  }
                : {
                    title: opts.product.productTitle ?? "",
                    type: "paid",
                    decisionBehaviorOfSubitems: "NONE",
                    estimatedQuantity: opts.quantity,
                    productValues: opts.product,
                  }
            ),
            expandedItemIds,
            rank: newItemRank,
          },
        },
      });
      await onItemCreated?.();
      return result.data?.createItems.items;
    },
    [createItems, expandedItemIds, onItemCreated, projectId, requirementId]
  );

  const [copiedItem] = useDocItemsClipboard();
  const [pasteItemFromClipboardMutation] =
    useProjectRequirementEdit_PasteItemFromClipboardMutation({ client });
  const pasteItem = React.useCallback(
    async (docId: string, parentItemId: string) => {
      if (!copiedItem) throw new Error("No item copied");

      const result = await pasteItemFromClipboardMutation({
        variables: {
          input: {
            sourceProjectId: copiedItem.projectId,
            sourceDocId: copiedItem.docId,
            sourceItemId: copiedItem.itemId,
            destProjectId: projectId,
            destDocId: docId,
            destItemId: parentItemId,
            expandedItemIds,
          },
        },
      });
      await onItemCreated?.();
    },
    [
      copiedItem,
      pasteItemFromClipboardMutation,
      projectId,
      expandedItemIds,
      onItemCreated,
    ]
  );

  const isAllowedToPasteItem = React.useCallback(
    (parentItem: { type: ItemType }) => {
      if (!copiedItem?.itemType || !parentItem.type) return false;

      return isAllowedToPaste({
        documentItemTypes: REQUIREMENT_ITEM_TYPES,
        copiedItemType: copiedItem.itemType,
        parentItemType: parentItem.type,
      });
    },
    [copiedItem?.itemType]
  );

  return {
    createItem,
    createItemByType,
    createItemFromTemplate,
    createItemsFromTemplates,
    createItemsWithProducts,
    pasteItem,
    isAllowedToPasteItem,
  };
}
